import React, { useEffect, useState } from "react";
import { api } from "@/api/client";
import { Section, Button, Input, Select, Textarea, Badge, Empty } from "@/components/ui/kit";
import { toast } from "sonner";
import { GitBranch, Plus } from "lucide-react";

export default function ProjectsTree() {
  const [tree, setTree] = useState([]);
  const [openAdd, setOpenAdd] = useState(false);
  const [f, setF] = useState({ type:"SECTOR", name:"", description:"", parentId:"" });
  const [flat, setFlat] = useState([]);

  const load = () => api.get("/donation-purposes/tree").then(r => setTree(r.data.data));
  useEffect(() => { load(); api.get("/donation-purposes").then(r => setFlat(r.data.data)); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/donation-purposes", { ...f, parentId: f.parentId ? Number(f.parentId) : null });
      toast.success("Created");
      setOpenAdd(false); setF({ type:"SECTOR", name:"", description:"", parentId:"" });
      load(); api.get("/donation-purposes").then(r => setFlat(r.data.data));
    } catch (err) { toast.error(err.response?.data?.message || "Failed"); }
  };

  const parents = flat.filter(p =>
    f.type === "PROJECT" ? p.type === "SECTOR"
    : f.type === "ACTIVITY" ? p.type === "PROJECT"
    : null);

  return (
    <div className="space-y-6">
      <Section title="Projects & Activities" kicker="Where donations flow"
        right={<Button onClick={()=>setOpenAdd(v=>!v)} data-testid="add-purpose-btn"><Plus className="w-4 h-4"/> Add purpose</Button>} />

      {openAdd && (
        <form onSubmit={save} className="rounded-xl border border-emerald-900/10 bg-white p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="purpose-add-form">
          <Select label="Type" testid="new-purpose-type" value={f.type} onChange={e=>setF(v=>({...v, type: e.target.value, parentId: ""}))}>
            <option value="SECTOR">Sector</option>
            <option value="PROJECT">Project</option>
            <option value="ACTIVITY">Activity</option>
          </Select>
          <Input label="Name" required testid="new-purpose-name" value={f.name} onChange={e=>setF(v=>({...v,name:e.target.value}))} />
          {(f.type === "PROJECT" || f.type === "ACTIVITY") && (
            <Select label="Parent" required testid="new-purpose-parent" value={f.parentId} onChange={e=>setF(v=>({...v, parentId: e.target.value}))}>
              <option value="">— select parent —</option>
              {parents.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </Select>
          )}
          <Textarea label="Description" testid="new-purpose-desc" rows={2} value={f.description} onChange={e=>setF(v=>({...v, description: e.target.value}))} />
          <div className="sm:col-span-2 lg:col-span-4">
            <Button type="submit" data-testid="save-purpose-btn">Save</Button>
          </div>
        </form>
      )}

      <div className="rounded-xl border border-emerald-900/10 bg-white p-6">
        {tree.length ? (
          <div className="space-y-4">
            {tree.map(sec => (
              <div key={sec.id} className="rounded-lg border border-emerald-900/10 bg-[#F7F4EF]/60 p-4" data-testid={`sector-${sec.id}`}>
                <div className="flex items-center gap-2">
                  <GitBranch className="w-4 h-4 text-emerald-800" />
                  <div className="font-display font-bold text-emerald-950">{sec.name}</div>
                  <Badge tone="brand">Sector</Badge>
                </div>
                {sec.description && <div className="text-xs text-muted-foreground mt-1">{sec.description}</div>}
                <div className="mt-3 space-y-2 pl-6 border-l-2 border-emerald-900/10">
                  {(sec.children || []).map(proj => (
                    <div key={proj.id} className="rounded-md bg-white border border-emerald-900/10 p-3" data-testid={`project-${proj.id}`}>
                      <div className="flex items-center gap-2">
                        <div className="font-semibold text-emerald-950">{proj.name}</div>
                        <Badge tone="indigo">Project</Badge>
                      </div>
                      {proj.description && <div className="text-xs text-muted-foreground">{proj.description}</div>}
                      <div className="mt-2 flex flex-wrap gap-2">
                        {(proj.children || []).map(act => (
                          <span key={act.id} data-testid={`activity-${act.id}`} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-orange-50 border border-orange-700/20 text-orange-800">
                            {act.name}
                          </span>
                        ))}
                        {!(proj.children || []).length && <span className="text-xs text-muted-foreground">No activities yet</span>}
                      </div>
                    </div>
                  ))}
                  {!(sec.children || []).length && <div className="text-xs text-muted-foreground">No projects yet</div>}
                </div>
              </div>
            ))}
          </div>
        ) : <Empty title="No sectors yet" note="Create a sector, then projects and activities beneath it." />}
      </div>
    </div>
  );
}
